import type {
  CreateMetaCreativeRequest,
  MetaBrandedContentAdPermission,
} from "../interfaces/MetaTypes";
import type { MarketingAdCreative } from "../interfaces/KYDMarketing";

export type MetaPartnershipAdCreativeFields = Pick<
  CreateMetaCreativeRequest,
  | "degrees_of_freedom_spec"
  | "facebook_branded_content"
  | "instagram_branded_content"
>;

type MetaCreativeFeatureEnrollStatus = "OPT_IN" | "OPT_OUT";

function metaCreativeFeatureEnroll(
  enroll_status: MetaCreativeFeatureEnrollStatus
): { enroll_status: MetaCreativeFeatureEnrollStatus } {
  return { enroll_status };
}

/**
 * Required on new partnership creatives (`USER_ENROLLED_AUTOFLOW`).
 * Partnership docs still show deprecated `standard_enhancements`; Graph API v22+ rejects that
 * field (#3858504) and expects per-feature `enroll_status` instead.
 *
 * @see https://developers.facebook.com/docs/marketing-api/ad-creative/partnership-ads/ads-creation/use-new-creative
 * @see https://developers.facebook.com/docs/marketing-api/advantage-catalog-ads/standard-enhancements/
 */
export const META_PARTNERSHIP_DEGREES_OF_FREEDOM_SPEC: NonNullable<
  CreateMetaCreativeRequest["degrees_of_freedom_spec"]
> = {
  degrees_of_freedom_type: "USER_ENROLLED_AUTOFLOW",
  // Ads Manager "Identity: Dynamic" — Meta may show advertiser or partner in the ad header.
  ad_handle_type: "DYNAMIC",
  creative_features_spec: {
    image_touchups: metaCreativeFeatureEnroll("OPT_IN"),
    image_templates: metaCreativeFeatureEnroll("OPT_IN"),
    inline_comment: metaCreativeFeatureEnroll("OPT_IN"),
    text_optimizations: metaCreativeFeatureEnroll("OPT_IN"),
    video_auto_crop: metaCreativeFeatureEnroll("OPT_IN"),
  },
};

/** Shown when Meta returns 1487194 on partnership creative create. */
export const META_PARTNERSHIP_PERMISSION_ERROR_HINT =
  "Confirm partnership is approved in Meta Business Suite, reconnect Meta with branded-content partnership permissions (facebook_branded_content_ads_brand / instagram_branded_content_ads_brand), and verify the partner Facebook Page id.";

/** When Meta returns 401 on api.facebook.com/partnership-ads/fb-account-level-permissions. */
export const META_FB_ALP_API_ACCESS_DENIED_MESSAGE =
  "Meta restricted Facebook partnership permission requests for this Page. The connecting user must administer the advertiser Facebook Page, and the Meta app needs Advanced Access for facebook_branded_content_ads_brand (plus business verification). Reconnect Meta in Settings, or send the request from Partnership Ads Hub in Meta Business Suite until API access is approved.";

export type MetaPartnershipPageRole = "advertiser" | "partner";

export function formatPartnershipPageInaccessibleError(args: {
  role: MetaPartnershipPageRole;
  pageId: string;
  metaMessage: string;
}): string {
  return `Partnership ad: Meta cannot access the ${args.role} Facebook Page (${args.pageId}). ${args.metaMessage} ${META_PARTNERSHIP_PERMISSION_ERROR_HINT}`;
}

export function formatPartnershipPageBrandedContentIneligibleError(args: {
  role: MetaPartnershipPageRole;
  pageId: string;
  pageName?: string;
}): string {
  const label = args.pageName
    ? `"${args.pageName}" (${args.pageId})`
    : args.pageId;
  return `Partnership ad: the ${args.role} Facebook Page ${label} is not eligible for branded content (partnership ads). Choose a different partner page or enable branded content for this page in Meta. ${META_PARTNERSHIP_PERMISSION_ERROR_HINT}`;
}

export function formatPartnershipBrandedContentPermissionError(args: {
  partnerPageName?: string;
  partnerInstagramUserId?: string;
  partnerFacebookPageId?: string;
  permissionStatus?: string;
}): string {
  const partnerLabel =
    args.partnerPageName && args.partnerFacebookPageId
      ? `Facebook Page "${args.partnerPageName}" (${args.partnerFacebookPageId})`
      : args.partnerInstagramUserId?.trim() ||
        (args.partnerFacebookPageId
          ? `Facebook Page ${args.partnerFacebookPageId}`
          : "partner");
  const statusSuffix = args.permissionStatus
    ? ` (status: ${args.permissionStatus})`
    : "";
  return `Partnership ad: no approved branded-content partnership with ${partnerLabel}${statusSuffix}. Approve the partnership in Meta Business Suite before publishing. ${META_PARTNERSHIP_PERMISSION_ERROR_HINT}`;
}

function normalizePartnershipUsername(value: string | undefined): string {
  return value?.trim().replace(/^@/, "").toLowerCase() ?? "";
}

/** Normalize Meta Graph numeric ids for comparison (e.g. "123" vs "123.0"). */
export function uniqueNonEmptyMetaIds(
  ...values: (string | undefined | null)[]
): string[] {
  return [
    ...new Set(
      values
        .map((value) => value?.trim())
        .filter((id): id is string => Boolean(id))
    ),
  ];
}

export function normalizeMetaGraphId(value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return "";
  }
  if (/^\d+(\.0+)?$/.test(trimmed)) {
    return trimmed.replace(/\.0+$/, "");
  }
  return trimmed;
}

function readMetaGraphNestedId(
  value: string | { id?: string } | undefined
): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    return value.trim() || undefined;
  }
  return value.id?.trim() || undefined;
}

/** Creator Instagram user id from a branded_content_ad_permissions row. */
export function metaBrandedContentPermissionCreatorIgId(
  permission: MetaBrandedContentAdPermission
): string | undefined {
  return (
    permission.creator_ig_id?.trim() ||
    readMetaGraphNestedId(permission.creator_id) ||
    permission.creator_instagram_account?.id?.trim() ||
    undefined
  );
}

/** Partner Facebook Page id from a branded_content_ad_permissions row. */
export function metaBrandedContentPermissionCreatorFbPageId(
  permission: MetaBrandedContentAdPermission
): string | undefined {
  return (
    permission.creator_fb_page_id?.trim() ||
    readMetaGraphNestedId(permission.creator_fb_page) ||
    undefined
  );
}

/** Map creator Instagram user id → Facebook Page id from page↔IG links. */
export function buildCreatorInstagramToFacebookPageIdMap(
  pages: readonly {
    id: string;
    linkedInstagramUserId?: string | null;
  }[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const page of pages) {
    const pageId = normalizeMetaGraphId(page.id);
    const linkedIg = normalizeMetaGraphId(
      page.linkedInstagramUserId ?? undefined
    );
    if (pageId && linkedIg) {
      map.set(linkedIg, pageId);
    }
  }
  return map;
}

function enrichCreatorIgToPageIdFromPermissions(
  map: Map<string, string>,
  permissions: readonly MetaBrandedContentAdPermission[]
): void {
  for (const permission of permissions) {
    const permissionPageId =
      metaBrandedContentPermissionCreatorFbPageId(permission);
    const permissionIg = metaBrandedContentPermissionCreatorIgId(permission);
    if (permissionPageId && permissionIg) {
      map.set(
        normalizeMetaGraphId(permissionIg) ?? permissionIg,
        normalizeMetaGraphId(permissionPageId) ?? permissionPageId
      );
    }
  }
}

/** Page↔IG links plus permission rows (shared by options API and publish preflight). */
export function buildPartnershipCreatorIgToPageIdMap(
  pageSources: readonly {
    id: string;
    linkedInstagramUserId?: string | null;
  }[],
  permissions: readonly MetaBrandedContentAdPermission[]
): Map<string, string> {
  const map = buildCreatorInstagramToFacebookPageIdMap(
    pageSources.map((page) => ({
      id: page.id,
      linkedInstagramUserId: page.linkedInstagramUserId ?? null,
    }))
  );
  enrichCreatorIgToPageIdFromPermissions(map, permissions);
  return map;
}

function pickBestPartnershipPermission(
  permissions: readonly MetaBrandedContentAdPermission[]
): MetaBrandedContentAdPermission | undefined {
  if (permissions.length === 0) {
    return undefined;
  }
  return [...permissions].sort(
    (a, b) =>
      partnershipPermissionStatusRank(a.permission_status) -
      partnershipPermissionStatusRank(b.permission_status)
  )[0];
}

function partnershipPermissionsForFacebookPage(
  permissions: readonly MetaBrandedContentAdPermission[],
  facebookPageId: string,
  creatorIgToPageId?: ReadonlyMap<string, string>
): MetaBrandedContentAdPermission[] {
  const pageIdNorm = normalizeMetaGraphId(facebookPageId);
  if (!pageIdNorm) {
    return [];
  }
  return permissions.filter((permission) => {
    const creatorFb = normalizeMetaGraphId(
      metaBrandedContentPermissionCreatorFbPageId(permission)
    );
    if (creatorFb && creatorFb === pageIdNorm) {
      return true;
    }
    const creatorIg = normalizeMetaGraphId(
      metaBrandedContentPermissionCreatorIgId(permission)
    );
    if (!creatorIg || !creatorIgToPageId) {
      return false;
    }
    return creatorIgToPageId.get(creatorIg) === pageIdNorm;
  });
}

export function partnershipBrandedContentPermissionMatchesPartner(
  permission: MetaBrandedContentAdPermission,
  partner: {
    instagramUserId?: string;
    facebookPageId?: string;
    instagramUsername?: string;
  }
): boolean {
  const partnerIg = normalizeMetaGraphId(partner.instagramUserId);
  const partnerFb = normalizeMetaGraphId(partner.facebookPageId);
  const creatorIg = normalizeMetaGraphId(
    metaBrandedContentPermissionCreatorIgId(permission)
  );
  const creatorFb = normalizeMetaGraphId(
    metaBrandedContentPermissionCreatorFbPageId(permission)
  );
  if (partnerIg && creatorIg && partnerIg === creatorIg) {
    return true;
  }
  if (partnerFb && creatorFb && partnerFb === creatorFb) {
    return true;
  }
  const partnerUsername = normalizePartnershipUsername(
    partner.instagramUsername
  );
  const creatorUsername = normalizePartnershipUsername(
    permission.creator_instagram_username ||
      permission.creator_username ||
      permission.creator_instagram_account?.username
  );
  if (partnerUsername && creatorUsername && partnerUsername === creatorUsername) {
    return true;
  }
  return false;
}

export type PartnershipBrandedContentPartnerRef = {
  instagramUserId?: string;
  facebookPageId?: string;
  instagramUsername?: string;
};

function partnershipPermissionStatusRank(
  status: string | undefined
): number {
  if (isApprovedBrandedContentPermissionStatus(status)) {
    return 0;
  }
  const normalized = status?.trim().toUpperCase();
  if (normalized === "PENDING") {
    return 1;
  }
  if (normalized === "REVOKED") {
    return 2;
  }
  return 3;
}

/** Prefer APPROVED over PENDING when multiple permission rows match the same partner. */
export function findPartnershipInstagramBrandedContentPermission(
  permissions: readonly MetaBrandedContentAdPermission[],
  partner: PartnershipBrandedContentPartnerRef,
  options?: { creator_ig_to_page_id?: ReadonlyMap<string, string> }
): MetaBrandedContentAdPermission | undefined {
  const matching = permissions.filter((permission) =>
    partnershipBrandedContentPermissionMatchesPartner(permission, partner)
  );
  const direct = pickBestPartnershipPermission(matching);
  if (direct) {
    return direct;
  }

  const partnerFb = partner.facebookPageId?.trim();
  if (!partnerFb) {
    return undefined;
  }
  return pickBestPartnershipPermission(
    partnershipPermissionsForFacebookPage(
      permissions,
      partnerFb,
      options?.creator_ig_to_page_id
    )
  );
}

/** Meta may return `APPROVED` or title-case `Approved` on branded_content_ad_permissions. */
export function isApprovedBrandedContentPermissionStatus(
  status: string | undefined
): boolean {
  return status?.trim().toUpperCase() === "APPROVED";
}

/** Guided-flow statuses that should block publish before calling Meta. */
export function isPublishBlockedPartnershipPermissionStatus(
  status: string | undefined
): boolean {
  const normalized = status?.trim().toUpperCase();
  return normalized === "PENDING" || normalized === "REVOKED";
}

/**
 * Partnership ads (advertiser as primary identity): top-level `facebook_branded_content.sponsor_page_id`
 * is the partner/creator page; `object_story_spec.page_id` is the advertiser page.
 * Do not use legacy `link_data.branded_content_sponsor_page_id` for this flow — Meta partnership docs use top-level fields only.
 */
export function buildMetaPartnershipAdCreativeFields(
  partnership: MarketingAdCreative["partnership"] | undefined
): MetaPartnershipAdCreativeFields {
  if (!partnership?.enabled) {
    return {};
  }

  const partnerIgId = partnership.partner_instagram_user_id?.trim();
  const partnerFbPageId = partnership.partner_facebook_page_id?.trim();
  if (!partnerIgId && !partnerFbPageId) {
    return {};
  }

  const base: MetaPartnershipAdCreativeFields = {
    degrees_of_freedom_spec: META_PARTNERSHIP_DEGREES_OF_FREEDOM_SPEC,
  };

  // Meta FB new-creative doc uses sponsor_page_id only; linked IG is auto-derived from the page.
  if (partnerFbPageId) {
    return {
      ...base,
      facebook_branded_content: { sponsor_page_id: partnerFbPageId },
    };
  }

  return {
    ...base,
    instagram_branded_content: { sponsor_id: partnerIgId },
  };
}

/**
 * Meta partnership creative docs use `object_story_spec` + `link_data`, not
 * `asset_feed_spec` + ad labels. Partnership ads skip `buildAdfeedSpec` so publish does
 * not require `POST /adlabels` on the advertiser account.
 *
 * Meta's Facebook new-creative doc omits top-level / object_story_spec
 * `instagram_user_id` when the partner is a Facebook Page (IG auto-links from the page).
 */
export function partnershipOmitsExplicitAdvertiserInstagramUserId(
  partnership: MarketingAdCreative["partnership"] | undefined
): boolean {
  if (!partnership?.enabled) {
    return false;
  }
  return Boolean(partnership.partner_facebook_page_id?.trim());
}

export function partnershipCreativeUsesObjectStorySpec(
  creative: Pick<MarketingAdCreative, "partnership">
): boolean {
  const partnership = creative.partnership;
  if (!partnership?.enabled) {
    return false;
  }
  return Boolean(
    partnership.partner_instagram_user_id?.trim() ||
      partnership.partner_facebook_page_id?.trim()
  );
}

export function isMetaPartnershipPermissionError(error: {
  error_subcode?: number;
}): boolean {
  return error.error_subcode === 1487194;
}
